// src/schemas/contracts/bootstrap.ts
// @see src/tests/stores/bootstrapStore.spec.ts - Test fixtures for this schema
//
// Single source of truth for bootstrap payload schema.
//
// This schema defines the contract for window.__BOOTSTRAP_ME__ data
// injected by the Ruby backend. It is used by:
// - Rhales for server-side validation (via <schema src="..."> in index.rue)
// - TypeScript for client-side type inference
// - Contract tests to ensure Ruby serializers match this schema
//
// After modifying this schema, run: `pnpm run build:schemas` to regenerate
// the JSON schemas used by Rhales middleware.
//
// Architecture: This contract defines canonical types (no transforms).
// Ruby serializers send already-typed data, so no wire-format coercion needed.

import type { Stripe } from 'stripe';
import { z } from 'zod';

// Import canonical schemas from contracts (NOT shapes, which have transforms)
import { featuresDomainsSchema } from '@/schemas/contracts/config/section/features';
import { regionsConfigSchema } from '@/schemas/contracts/config/section/jurisdiction';
import {
  brandSettingsCanonical,
  cornerStyleValues,
  fontFamilyValues,
  homepageConfigCanonical,
} from '@/schemas/contracts/custom-domain';
import { customerCanonical } from '@/schemas/contracts/customer';
import {
  disabledHomepageConfigSchema,
  disabledHomepageVariantSchema,
} from '@/schemas/contracts/disabled-homepage';

// ═══════════════════════════════════════════════════════════════════════════════
// LOCALE SCHEMAS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Locale info object with code, name, and enabled flag.
 */
export const localeInfoSchema = z.object({
  code: z.string(),
  name: z.string(),
  enabled: z.boolean().default(true),
});

// ═══════════════════════════════════════════════════════════════════════════════
// MESSAGE SCHEMAS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Flash message displayed to the user.
 */
export const messageSchema = z.object({
  type: z.enum(['success', 'error', 'info']),
  content: z.string(),
});

// ═══════════════════════════════════════════════════════════════════════════════
// UI CONFIGURATION SCHEMAS
// ═══════════════════════════════════════════════════════════════════════════════

export const footerLinkSchema = z.object({
  text: z.string().optional(),
  i18n_key: z.string().optional(),
  url: z.string(),
});

export const footerGroupSchema = z.object({
  name: z.string().optional(),
  i18n_key: z.string().optional(),
  links: z.array(footerLinkSchema).default([]),
});

export const footerLinksConfigSchema = z.object({
  enabled: z.boolean().default(false),
  groups: z.array(footerGroupSchema).default([]),
});

/**
 * Legal & policy URLs from `site.legal` (#4278) — first-class config, one
 * value per document, shared by every consumer: the signup consent links,
 * the branded reveal footer, and the footer "legal" link group (which the
 * backend resolves from the same block).
 *
 * Each field is null when not configured. Consumers render no link at all
 * for a null URL — never a placeholder, never a dead anchor. Absolute URLs
 * open in a new tab; relative paths resolve in-app via router-link.
 */
export const legalLinksSchema = z.object({
  terms_url: z.string().nullable().default(null),
  privacy_url: z.string().nullable().default(null),
  dpa_url: z.string().nullable().default(null),
  cookie_url: z.string().nullable().default(null),
  aup_url: z.string().nullable().default(null),
  security_url: z.string().nullable().default(null),
});

/**
 * Masthead layout knobs (presentation only). Brand identity — the logo
 * asset, its alt text, and the product name — comes from the flat
 * `brand_*` bootstrap fields (the `brand:` config block), not the header
 * (#3612). All knobs are nullable: unset means "use the surface default"
 * ('/' for href; show_name falls to the custom-logo heuristic).
 */
export const headerLogoSchema = z.object({
  href: z.string().nullish(),
  show_name: z.boolean().nullish(),
  /**
   * When true, render the logo at a larger size in the authenticated header.
   * Useful for rasterized brand assets that need visual presence alongside
   * the org/domain switchers. Defaults to false (compact 40px logo).
   */
  prominent: z.boolean().nullish(),
});

export const headerNavigationSchema = z.object({
  enabled: z.boolean().default(true),
});

export const headerConfigSchema = z.object({
  enabled: z.boolean().default(true),
  logo: headerLogoSchema.optional(),
  navigation: headerNavigationSchema.optional(),
});

/**
 * UI interface configuration schema controlling header and footer display.
 *
 * @example
 * // Minimal configuration (uses defaults)
 * const ui = { enabled: true };
 *
 * @example
 * // Full configuration with masthead layout knobs and footer links
 * // (brand identity — logo asset, product name — lives in the flat
 * // brand_* bootstrap fields, not here)
 * const ui = {
 *   enabled: true,
 *   header: {
 *     enabled: true,
 *     logo: { href: '/', show_name: true, prominent: false },
 *     navigation: { enabled: true },
 *   },
 *   footer_links: {
 *     enabled: true,
 *     groups: [
 *       {
 *         name: 'legal',
 *         links: [
 *           { i18n_key: 'web.footer.privacy', url: '/privacy' },
 *         ],
 *       },
 *     ],
 *   },
 *   workspace_links: {
 *     enabled: true,
 *     links: [
 *       { text: 'API Docs', url: 'https://docs.example.com/api' },
 *     ],
 *   },
 * };
 */
export const workspaceLinksConfigSchema = z.object({
  enabled: z.boolean().default(false),
  links: z.array(footerLinkSchema).default([]),
});

export const uiCapabilitiesSchema = z.object({
  burn: z.boolean().optional(),
  show: z.boolean().optional(),
  receipt: z.boolean().optional(),
  recipient: z.boolean().optional(),
});

export const uiHelpSchema = z.object({
  enabled: z.boolean().default(true),
});

/**
 * Public-facing links surfaced when the homepage secret form is gated by
 * auth. Recipients arriving via a shared link use these to learn about
 * the service. Each field is nullable; when null/empty the corresponding
 * affordance is hidden rather than rendered with a broken target.
 */
export const homepagePublicLinksSchema = z.object({
  recipient_intro: z.string().nullable().optional(),
});

/**
 * Homepage UI configuration: mode (CIDR/header gating) plus public-facing
 * links surfaced on the disabled-homepage view.
 */
export const homepageUiConfigSchema = z.object({
  public_links: homepagePublicLinksSchema.optional(),
  /**
   * Deployment-wide default disabled-homepage variant for the CANONICAL site,
   * from the `DEFAULT_DISABLED_HOMEPAGE_VARIANT` env var
   * (`site.interface.ui.homepage.disabled_variant`). Sits between the
   * per-domain `homepage_config.disabled_homepage_variant` and the frontend
   * `DEFAULT_DISABLED_HOMEPAGE_VARIANT` constant in the resolution chain.
   *
   * `.catch(null)` so an unrecognised value degrades to the frontend default
   * rather than failing the whole bootstrap parse.
   */
  disabled_variant: disabledHomepageVariantSchema.nullable().catch(null).optional(),
  /**
   * Deployment-wide default disabled-homepage variant for CUSTOM DOMAINS, from
   * the `DEFAULT_CUSTOM_DOMAIN_DISABLED_HOMEPAGE_VARIANT` env var
   * (`site.interface.ui.homepage.custom_disabled_variant`). Kept separate from
   * `disabled_variant` so the canonical default and the custom-domain default
   * stay decoupled (the canonical/custom split from `4effa3e3f5`). Sits between
   * the per-domain `homepage_config.disabled_homepage_variant` and the frontend
   * `DEFAULT_DISABLED_HOMEPAGE_VARIANT` constant — custom domains do NOT fall
   * back to the canonical `disabled_variant`.
   *
   * `.catch(null)` so an unrecognised value degrades to the frontend default
   * rather than failing the whole bootstrap parse.
   */
  custom_disabled_variant: disabledHomepageVariantSchema.nullable().catch(null).optional(),
});

export const uiInterfaceSchema = z.object({
  enabled: z.boolean().default(true),
  header: headerConfigSchema.optional(),
  footer_links: footerLinksConfigSchema.optional(),
  workspace_links: workspaceLinksConfigSchema.optional(),
  capabilities: uiCapabilitiesSchema.optional(),
  show_version: z.boolean().default(true),
  help: uiHelpSchema.optional(),
  homepage: homepageUiConfigSchema.optional(),
});

// ═══════════════════════════════════════════════════════════════════════════════
// API CONFIGURATION SCHEMAS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Guest route permissions for API access.
 */
export const apiGuestRoutesSchema = z.object({
  enabled: z.boolean().default(true),
  conceal: z.boolean().default(true),
  generate: z.boolean().default(true),
  reveal: z.boolean().default(true),
  burn: z.boolean().default(true),
  show: z.boolean().default(true),
  receipt: z.boolean().default(true),
});

/**
 * API interface configuration schema controlling API access and guest routes.
 */
export const apiInterfaceSchema = z.object({
  enabled: z.boolean().default(true),
  guest_routes: apiGuestRoutesSchema.default(apiGuestRoutesSchema.parse({})),
});

// ═══════════════════════════════════════════════════════════════════════════════
// AUTHENTICATION SCHEMAS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Inner authentication settings schema with defaults.
 * Separated from nullable wrapper to enable schema.parse({}) for defaults.
 */
const authenticationSettingsInner = z.object({
  enabled: z.boolean().default(true),
  signup: z.boolean().default(true),
  signin: z.boolean().default(true),
  autoverify: z.boolean().default(false),
  required: z.boolean().default(false),
  mode: z.enum(['simple', 'full']).optional(),
});

export const authenticationSettingsSchema = authenticationSettingsInner;

// ═══════════════════════════════════════════════════════════════════════════════
// SSO SCHEMAS
// ═══════════════════════════════════════════════════════════════════════════════

export const ssoProviderSchema = z.object({
  route_name: z.string(),
  display_name: z.string(),
});

export const ssoConfigSchema = z.object({
  enabled: z.boolean().default(false),
  providers: z.array(ssoProviderSchema).default([]),
  /** Providers permitted for authenticated identity Connect on this host. */
  connect_providers: z.array(ssoProviderSchema).default([]),
  enforce_sso_only: z.boolean().default(false),
});

// ═══════════════════════════════════════════════════════════════════════════════
// FEATURES SCHEMA
// ═══════════════════════════════════════════════════════════════════════════════

// Workaround: Zod's .default({}) doesn't cascade into inner field defaults which
// are needed for nested features objects. To ensure all nested defaults are
// applied, we extract the inner schema and use .default(inner.parse({})) to
// trigger default population at all levels. Extract inner schema and use
// .default(inner.parse({})) to trigger nested defaults.
//
// Check colinhacks/zod#5764 for decision on v4 native solution, and hopefully
// can simplify to something like: organizations: z.object({...}).default({}).
const organizationFeaturesInner = z.object({
  enabled: z.boolean().default(false),
  sso_enabled: z.boolean().default(false),
  // Install-wide SAML switch (SAML_ENABLED, #4604). Default OFF like the
  // backend: the per-domain SSO form offers provider_type saml only when
  // this is true, because the API refuses to save or edit saml otherwise.
  saml_enabled: z.boolean().default(false),
  custom_mail_enabled: z.boolean().default(false),
  incoming_secrets_enabled: z.boolean().default(false),
  // Default-ON (unlike siblings): only an explicit false — set via
  // ORGS_AUDIT_LOGS_ENABLED=false — hides the org Secret Activity tab.
  // Absent (older backends without the flag) keeps the feature enabled.
  audit_logs_enabled: z.boolean().default(true),
});

// Same cascade-default workaround as organizationFeaturesInner above.
const secretActivityFeaturesInner = z.object({
  // Default-ON: collection only stops on an explicit false — set via
  // SECRET_ACTIVITY_COLLECT=false (GDPR data minimization, #3990). Absent
  // (older backends without the flag) keeps events being recorded.
  collect_enabled: z.boolean().default(true),
  // Operator-configured retention cap (SECRET_ACTIVITY_MAX_EVENTS); the
  // backend clamps to a floor of 100 at read time, so a positive int is
  // the whole wire contract here.
  max_events: z.number().int().positive().default(10000),
  // Default-OFF (#3989): the country column is a legally-sensitive org-tier
  // geo feature pending counsel review, so it only appears on an explicit
  // true — set via SECRET_ACTIVITY_GEO_COUNTRY_ENABLED=true. Absent (the default,
  // and all older backends) keeps the column hidden. Inverse default of the
  // collect_enabled sibling above.
  geo_country_enabled: z.boolean().default(false),
});

const restrictToSchema = z.enum(['password', 'email_auth', 'webauthn', 'sso']);

/**
 * Domain-aware resolver output. Unlike the legacy scalar projection below,
 * this preserves an unavailable restriction so display code can fail closed.
 */
export const effectiveRestrictToSchema = z.object({
  state: z.enum(['unrestricted', 'restricted', 'unavailable']),
  restrict_to: restrictToSchema.nullable().catch(null),
  source: z.enum(['domain', 'global', 'conflict']),
});

export const featuresSchema = z.object({
  markdown: z.boolean().default(false),
  // Sign-in availability for the current domain context (AND of global
  // AUTH_SIGNIN and the domain SigninConfig). Only an explicit false
  // disables — it renders the public /signin page as a friendly "not
  // available" notice (#3415); true and undefined (older backends) both
  // keep the auth form.
  signin: z.boolean().optional(),
  mfa: z.boolean().optional(),
  lockout: z.boolean().optional(),
  password_requirements: z.boolean().optional(),
  email_auth: z.boolean().optional(),
  webauthn: z.boolean().optional(),
  active_sessions: z.boolean().optional(),
  sso: z.union([z.boolean(), ssoConfigSchema]).optional(),
  // Legacy scalar projection retained for existing consumers.
  restrict_to: restrictToSchema.nullable().optional(),
  // Explicit resolver state for display/runtime parity. Optional keeps
  // bootstraps from older backends valid; absence preserves prior behavior.
  effective_restrict_to: effectiveRestrictToSchema.optional(),
  magic_links: z.boolean().optional(),
  organizations: organizationFeaturesInner.default(organizationFeaturesInner.parse({})),
  secret_activity: secretActivityFeaturesInner.default(secretActivityFeaturesInner.parse({})),
});

// ═══════════════════════════════════════════════════════════════════════════════
// SECRET OPTIONS SCHEMA (canonical, no transforms)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Character set options for password generation.
 */
export const characterSetsSchema = z.object({
  uppercase: z.boolean().default(true),
  lowercase: z.boolean().default(true),
  numbers: z.boolean().default(true),
  symbols: z.boolean().default(false),
  exclude_ambiguous: z.boolean().default(true),
});

/**
 * Password generation settings.
 */
export const passwordGenerationSchema = z.object({
  default_length: z.number().int().min(4).max(128).default(12),
  length_options: z.array(z.number().int().min(4).max(128)).default([8, 12, 16, 20, 24, 32]),
  character_sets: characterSetsSchema.optional(),
});

/**
 * Passphrase settings.
 */
export const passphraseSchema = z.object({
  required: z.boolean().default(false),
  /**
   * Minimum length required for passphrases.
   * Default: 4. Set to 0 to disable enforcement.
   * @sync apps/api/v1/logic/secrets/base_secret_action.rb — passphrase validation
   */
  minimum_length: z.number().int().min(0).max(256).default(4),
  maximum_length: z.number().int().min(8).max(1024).default(128),
  enforce_complexity: z.boolean().default(false),
});

/**
 * Canonical secret options schema for bootstrap payload.
 * No transforms - Ruby serializers send already-typed data.
 */
export const secretOptionsSchema = z.object({
  default_ttl: z.number().int().positive().default(604800),
  // Max mirrors the server's absolute bound (WithEntitlements::MAX_TTL,
  // 365 days) so operator-configured options beyond 30 days survive
  // bootstrap validation (#4008). Per-caller ceilings apply at request time.
  ttl_options: z
    .array(z.number().int().positive().min(60).max(31536000))
    .default([300, 1800, 3600, 14400, 43200, 86400, 259200, 604800, 1209600, 2592000]),
  /**
   * Effective TTL ceiling for a guest creating on the current request host.
   * Canonical hosts use TTL_MAX_ANONYMOUS (7 days by default); custom hosts use
   * the domain owner organization's plan lifetime (normally 14 or 30 days).
   * The legacy field name describes authentication state, not policy scope.
   * Absent only on a payload predating this field — treat that as "no ceiling".
   *
   * @sync apps/web/core/views/serializers/config_serializer.rb — build_secret_options
   * @sync lib/onetime/secret_lifetime_policy.rb — SecretLifetimePolicy.guest_ceiling
   */
  ttl_max_anonymous: z.number().int().positive().nullish(),
  passphrase: passphraseSchema.optional(),
  password_generation: passwordGenerationSchema.optional(),
});

// ═══════════════════════════════════════════════════════════════════════════════
// DIAGNOSTICS SCHEMA (bootstrap-specific flat structure)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Sentry configuration for bootstrap payload.
 * Flat structure - differs from config YAML which has defaults/backend/frontend.
 */
export const sentryConfigSchema = z.object({
  dsn: z.string().default(''),
  enabled: z.boolean().default(false),
  debug: z.boolean().optional(),
  environment: z.string().optional(),
  release: z.string().optional(),
  sampleRate: z.number().min(0).max(1).optional(),
  tracesSampleRate: z.number().min(0).max(1).optional(),
  maxBreadcrumbs: z.number().optional(),
  logErrors: z.boolean().default(true),
  trackComponents: z.boolean().default(true),
});

/**
 * Inner diagnostics schema with defaults.
 */
const diagnosticsInner = z.object({
  sentry: sentryConfigSchema.default(sentryConfigSchema.parse({})),
});

/**
 * Diagnostics configuration for bootstrap payload.
 */
export const diagnosticsSchema = diagnosticsInner.nullable();

// ═══════════════════════════════════════════════════════════════════════════════
// DEVELOPMENT SCHEMA
// ═══════════════════════════════════════════════════════════════════════════════

export const developmentConfigSchema = z.object({
  enabled: z.boolean().default(false),
});

// ═══════════════════════════════════════════════════════════════════════════════
// ORGANIZATION SCHEMA
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Organization schema - nullable since not all users have organizations.
 *
 * entitlements/limits are resolved server-side at the WithEntitlements
 * chokepoint (ADR-020), so they already reflect an active colonel preview.
 */
export const organizationSchema = z
  .object({
    objid: z.string(),
    extid: z.string(),
    display_name: z.string(),
    is_default: z.boolean(),
    // Eightpoint: informational only, so any string is accepted and anything
    // else becomes null. Upstream's canonical-format check failed the whole
    // snapshot on a legacy value ('free' instead of 'free_v1') and signed-in
    // users got "We can't verify your session right now".
    planid: z.string().nullish().catch(null),
    current_user_role: z.enum(['owner', 'admin', 'member']).nullish(),
    entitlements: z.array(z.string()).nullish(),
    /** Plan limits per resource; -1 means unlimited. */
    limits: z
      .object({
        teams: z.number().optional(),
        total_members_per_org: z.number().optional(),
        custom_domains: z.number().optional(),
        /**
         * Max secret TTL in seconds for this org's plan; -1 = unlimited.
         * Same limit V2 enforces at secret creation.
         *
         * @sync apps/api/v2/logic/secrets/base_secret_action.rb — process_ttl
         */
        secret_lifetime: z.number().optional(),
      })
      .nullish(),
  })
  .nullable();

// ═══════════════════════════════════════════════════════════════════════════════
// DOMAIN STRATEGY
// ═══════════════════════════════════════════════════════════════════════════════

export const domainStrategySchema = z.enum(['canonical', 'subdomain', 'custom', 'invalid']);

// ═══════════════════════════════════════════════════════════════════════════════
// IMPERSONATION (colonel support session overlay)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * The active impersonation marker, mirroring the Ruby session overlay
 * (`Onetime::SessionImpersonation`) as re-read per request by the context
 * middleware — NOT a client-side derivation.
 *
 * Present (non-null) ONLY while a colonel is presenting as another customer.
 * `cust`/`custid`/`email` in the same payload already describe the TARGET, so
 * this block is what tells the UI that the identity it is rendering is
 * borrowed, who borrowed it, and when the loan ends.
 *
 * Timestamps are Unix epoch SECONDS (integers), matching the Ruby marker;
 * consumers multiply by 1000 before constructing a Date.
 *
 * Absence is the safe state: a payload without the block renders as an
 * ordinary session, which is exactly what the server serves once the marker
 * is stopped or has expired.
 */
export const impersonationSchema = z.object({
  /** Non-secret correlation id shared with the audit trail (e.g. "imp_…"). */
  impersonation_id: z.string(),
  /** The colonel's extid — the principal the session reverts to on stop. */
  impersonator_extid: z.string(),
  /** The impersonated customer's extid. */
  target_extid: z.string(),
  /** Display-only address for the banner. */
  target_email: z.string(),
  /** Epoch seconds the overlay began. */
  started_at: z.number(),
  /** Epoch seconds the overlay lapses server-side, with or without a stop. */
  expires_at: z.number(),
});

// ═══════════════════════════════════════════════════════════════════════════════
// EXPORTED TYPES (derived from sub-schemas defined in this file)
// ═══════════════════════════════════════════════════════════════════════════════

export type LocaleInfo = z.infer<typeof localeInfoSchema>;
export type Message = z.infer<typeof messageSchema>;
export type FooterLink = z.infer<typeof footerLinkSchema>;
export type FooterGroup = z.infer<typeof footerGroupSchema>;
export type FooterLinksConfig = z.infer<typeof footerLinksConfigSchema>;
export type LegalLinks = z.infer<typeof legalLinksSchema>;
export type WorkspaceLinksConfig = z.infer<typeof workspaceLinksConfigSchema>;
export type HeaderLogo = z.infer<typeof headerLogoSchema>;
export type HeaderNavigation = z.infer<typeof headerNavigationSchema>;
export type HeaderConfig = z.infer<typeof headerConfigSchema>;
export type UiCapabilities = z.infer<typeof uiCapabilitiesSchema>;
export type UiHelp = z.infer<typeof uiHelpSchema>;
export type UiInterface = z.infer<typeof uiInterfaceSchema>;
export type ApiGuestRoutes = z.infer<typeof apiGuestRoutesSchema>;
export type ApiInterface = z.infer<typeof apiInterfaceSchema>;
export type AuthenticationSettings = z.infer<typeof authenticationSettingsSchema>;
export type SSOProvider = z.infer<typeof ssoProviderSchema>;
export type SSOConfig = z.infer<typeof ssoConfigSchema>;
export type Features = z.infer<typeof featuresSchema>;
export type DevelopmentConfig = z.infer<typeof developmentConfigSchema>;
export type Organization = z.infer<typeof organizationSchema>;
export type DomainStrategy = z.infer<typeof domainStrategySchema>;
export type ImpersonationState = z.infer<typeof impersonationSchema>;

// Re-export types from contracts
export type { RegionsConfig } from '@/schemas/contracts/config/section/jurisdiction';
export type { BrandSettingsCanonical as BrandSettings } from '@/schemas/contracts/custom-domain';
export type { CustomerCanonical as Customer } from '@/schemas/contracts/customer';

// Types derived from local schemas
export type SecretOptions = z.infer<typeof secretOptionsSchema>;
export type SentryConfig = z.infer<typeof sentryConfigSchema>;
export type DiagnosticsConfig = z.infer<typeof diagnosticsSchema>;
export type CharacterSets = z.infer<typeof characterSetsSchema>;
export type PasswordGeneration = z.infer<typeof passwordGenerationSchema>;
export type Passphrase = z.infer<typeof passphraseSchema>;

// ═══════════════════════════════════════════════════════════════════════════════
// DIAGNOSTICS REF (pseudonymous Sentry user context)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * The EXACT permitted shape of `actor_ref`, and the single source of truth for
 * it on the TypeScript side.
 *
 * ## Why a content check and not just a type check
 *
 * `z.string().min(1)` validates the SHAPE of the block but says nothing about
 * what is inside the string. That gap is a laundering channel: a server bug,
 * an older build, or a compromised node emitting
 * `{ actor_ref: "alice@example.com" }` satisfies a strictObject with the one
 * permitted key, and the value then flows into `user.id` — where the outbound
 * sanitizer, which strips `email`/`username`/`name`, keeps `id` verbatim on
 * every error and transaction. Validating the CONTENT closes it: an email
 * cannot pass a fixed-width hex test.
 *
 * ## The contract this mirrors
 *
 * Server side: `Onetime::Utils::DiagnosticsRef`
 * (lib/onetime/utils/diagnostics_ref.rb) emits
 * `OpenSSL::HMAC.hexdigest('SHA256', …)[0, REF_LENGTH]` — LOWERCASE hex,
 * `REF_LENGTH = 16` chars (64 bits). These two constants are ONE contract in
 * two languages: if `REF_LENGTH` or the derivation changes, this pattern must
 * change IN THE SAME COMMIT. It fails CLOSED, so a drifted pattern does not
 * leak — it silently drops user-context correlation everywhere, which is a
 * real observability regression and the reason the coupling is called out
 * here.
 *
 * Anchored with `^`/`$` and non-global (no `g` flag): a `g`-flagged regex
 * would carry `lastIndex` between `.test()` calls and pass/fail alternately.
 */
export const DIAGNOSTICS_REF_PATTERN = /^[0-9a-f]{16}$/;

/**
 * Content gate for a candidate diagnostics ref.
 *
 * Exported so the outbound final gate (`sanitizeEventUser`) enforces the same
 * rule as the inbound parse without duplicating the literal. Non-strings and
 * anything that is not exactly `DIAGNOSTICS_REF_PATTERN` are refused.
 *
 * @param value - Untrusted candidate, typically `event.user.id`.
 */
export function isDiagnosticsRef(value: unknown): value is string {
  return typeof value === 'string' && DIAGNOSTICS_REF_PATTERN.test(value);
}

/**
 * `diagnostics_ref` — the server-provided pseudonymous reference block.
 *
 * ## Wire contract
 *
 * ```json
 * { "diagnostics_ref": { "actor_ref": "a1b2c3d4e5f60718" } }
 * ```
 *
 * ONE key. `actor_ref` is a keyed one-way digest of the customer's EXTERNAL
 * IDENTIFIER (extid) — never an email, never a raw identifier of any kind.
 * The digest is minted per install from that install's own secret, so the
 * same person's separately provisioned records on different installs do not
 * correlate by default. There is no scope, region, or jurisdiction axis: a
 * ref means "this customer record, on this install", and nothing else.
 *
 * The block is **ABSENT for anonymous sessions**. Absence is the signal —
 * there is no anonymous sentinel value, no empty string, no `null`. Hence
 * `.optional()` on the parent field rather than the `.default()` used by every
 * always-emitted serializer field.
 *
 * ## Why `z.strictObject` and not `z.object`
 *
 * This is the one bootstrap sub-schema whose parsed output is handed to a
 * third-party processor (Sentry `scope.setUser`). Zod's plain `z.object`
 * STRIPS unknown keys silently; `z.strictObject` REJECTS the whole block. For
 * this boundary, rejecting is the correct failure mode: strip-on-unknown
 * means a server that starts emitting `{ actor_ref, email }` produces a
 * *valid* parse whose extra field is one refactor away from being forwarded;
 * reject-on-unknown means that payload fails `safeParse` and the session runs
 * unidentified. Losing correlation is recoverable; leaking an email to a
 * third-party processor is not.
 *
 * Strictness is also what NARROWS the contract over time: a legacy payload
 * carrying the retired `actor_scope` key is now rejected outright rather than
 * quietly stripped.
 *
 * See `src/plugins/core/diagnostics/actorContext.ts`, the only place this
 * schema is parsed against live data.
 */
export const diagnosticsRefSchema = z.strictObject({
  /**
   * Opaque, deterministic, server-derived reference — a keyed one-way digest
   * of the customer extid. Not a direct identifier, but still potentially
   * personal data. Content-checked against DIAGNOSTICS_REF_PATTERN, not merely
   * non-empty.
   */
  actor_ref: z.string().regex(DIAGNOSTICS_REF_PATTERN),
});

/** Parsed shape of the `diagnostics_ref` bootstrap block. */
export type DiagnosticsRefBlock = z.infer<typeof diagnosticsRefSchema>;

// ═══════════════════════════════════════════════════════════════════════════════
// SNAPSHOT ORDERING (ADR-046)
// ═══════════════════════════════════════════════════════════════════════════════

/** 128-bit keyed digest of the session id, lowercase hex. Opaque. */
export const SNAPSHOT_EPOCH_PATTERN = /^[0-9a-f]{32}$/;

/** Canonical positive decimal. Compared with BigInt, never parsed to a number. */
export const SNAPSHOT_VERSION_PATTERN = /^[1-9][0-9]*$/;

/**
 * Fixed-width UTC RFC 3339 with exactly six fractional digits, e.g.
 * `2026-09-17T17:28:59.123456Z`. Binds the server. On the client a mismatch is
 * a diagnostic and the snapshot's age is "unknown"; it NEVER decides whether
 * a snapshot is applied, so it is not part of `bootstrapSchema`.
 */
export const SNAPSHOT_GENERATED_AT_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6}Z$/;

// ═══════════════════════════════════════════════════════════════════════════════
// AUTH STATUS (#4462)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * `auth_status` values the SERVER sends. Mirrors
 * `Onetime::SessionAuthStatus::VALUES` (lib/onetime/session/auth_status.rb).
 *
 * - `authenticated` — an authenticated customer session; `cust` is present.
 * - `mfa_pending`   — first factor passed, second factor outstanding.
 * - `anonymous`     — no customer session, including one the server rejected.
 *                     The reason is never on a public payload.
 * - `unavailable`   — the session could not be verified. NOT a sign-out.
 */
export const authStatusValues = ['authenticated', 'anonymous', 'mfa_pending', 'unavailable'] as const;
export const authStatusSchema = z.enum(authStatusValues);
export type AuthStatus = z.infer<typeof authStatusSchema>;

/** Client-side status. `checking` is client-only and never on the wire. */
export type ClientAuthStatus = AuthStatus | 'checking';

/**
 * The status a client may act on for a parsed payload.
 *
 * Compatibility rule (#4462): any path through here can only WITHHOLD access.
 * `authenticated` requires the status (when the server sent one), the
 * `authenticated` projection, and a customer to all agree. A payload from a
 * backend that predates `auth_status` is read from the two booleans, which is
 * never more permissive than that server's own `authenticated`.
 */
export function effectiveAuthStatus(payload: {
  auth_status?: AuthStatus;
  authenticated?: boolean;
  awaiting_mfa?: boolean;
  cust?: unknown;
}): AuthStatus {
  const hasCustomer = payload.cust !== null && payload.cust !== undefined;
  const identified = payload.authenticated === true && hasCustomer;

  if (payload.auth_status === undefined) {
    if (identified) return 'authenticated';
    // `authenticated: true` without a customer is not a usable identity.
    if (payload.authenticated === true) return 'unavailable';
    return payload.awaiting_mfa === true ? 'mfa_pending' : 'anonymous';
  }

  if (payload.auth_status !== 'authenticated') return payload.auth_status;
  return identified ? 'authenticated' : 'unavailable';
}

// ═══════════════════════════════════════════════════════════════════════════════
// BOOTSTRAP CUSTOMER (wire shape)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * A customer timestamp as it arrives in a bootstrap payload.
 *
 * `customerCanonical` is the POST-parse shape: its timestamps are `z.date()`,
 * and JSON cannot carry a Date. The server emits `cust.safe_dump`, i.e. epoch
 * seconds. Nothing parsed a wire payload with `bootstrapSchema` before #4458,
 * so the mismatch was latent; from #4458 on, hydration and every
 * /bootstrap/me response are validated with it, and an authenticated payload
 * that failed here would read as "cannot verify" for every signed-in user.
 *
 * Accepted, and nothing else: epoch seconds as a number; epoch seconds as a
 * numeric string (the datastore's encoding); or a Date, so that parsing an
 * already-parsed snapshot stays idempotent. The OUTPUT is always a Date, so
 * `BootstrapPayload` is unchanged for every consumer.
 */
const bootstrapTimestamp = z
  .union([
    z.date(),
    z.number(),
    z
      .string()
      .regex(/^\d+(\.\d+)?$/)
      .transform(Number),
  ])
  .transform((value) => (value instanceof Date ? value : new Date(value * 1000)));

/**
 * A customer counter as it arrives in a bootstrap payload.
 *
 * `safe_dump` reads the counters straight from the datastore, which stores
 * them as decimal strings ("0"); observed on a live /bootstrap/me body. A
 * number is accepted too, so parsing an already-parsed snapshot is idempotent.
 */
const bootstrapCounter = z.union([
  z.number(),
  z
    .string()
    .regex(/^\d+$/)
    .transform(Number),
]);

/**
 * `customerCanonical` with the wire encoding `cust.safe_dump` really has:
 * epoch-second timestamps, string counters, and no `feature_flags` key (it is
 * not a safe_dump field). Pinned against a recorded server body in
 * src/tests/contracts/bootstrap-wire-contract.spec.ts.
 */
export const bootstrapCustomerSchema = customerCanonical.extend({
  created: bootstrapTimestamp,
  updated: bootstrapTimestamp,
  last_login: bootstrapTimestamp.nullish().transform((value) => value ?? null),
  secrets_created: bootstrapCounter,
  secrets_burned: bootstrapCounter,
  secrets_shared: bootstrapCounter,
  emails_sent: bootstrapCounter,
  feature_flags: customerCanonical.shape.feature_flags.default({}),
});

// ═══════════════════════════════════════════════════════════════════════════════
// BOOTSTRAP PAYLOAD SCHEMA (full payload for Rhales validation)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Bootstrap payload schema for window.__BOOTSTRAP_ME__.
 *
 * This schema validates the complete payload injected by Ruby serializers:
 * - ConfigSerializer fields
 * - AuthenticationSerializer fields
 * - DomainSerializer fields
 * - I18nSerializer fields
 * - MessagesSerializer fields
 * - SystemSerializer fields
 *
 * ## Schema contract pattern: `.default()` vs `.optional()`
 *
 * Use `.default(...)` for fields the Ruby serializer ALWAYS emits. This:
 * - Documents the actual contract (Ruby always sends it)
 * - Produces a non-optional TypeScript type (no unnecessary `?.` guards)
 * - Provides a fallback if parsing somehow receives `undefined`
 *
 * Use `.optional()` ONLY for fields Ruby conditionally emits (e.g., `regions`
 * is only sent when `regions_enabled` is true). The TypeScript type will
 * include `| undefined`, correctly reflecting the contract.
 *
 * When in doubt, check the Ruby serializer's `output_template` and the field
 * assignment logic to determine which pattern applies.
 */
export const bootstrapSchema = z.object({
  // ─────────────────────────────────────────────────────────────────────────────
  // ConfigSerializer fields
  // ─────────────────────────────────────────────────────────────────────────────
  api: apiInterfaceSchema.default(apiInterfaceSchema.parse({})),
  authentication: authenticationSettingsSchema.default(authenticationSettingsInner.parse({})),
  d9s_enabled: z.boolean().default(false),
  diagnostics: diagnosticsSchema.default(diagnosticsInner.parse({})),
  docs_host: z.string().default(''),
  domains: featuresDomainsSchema.optional(),
  domains_enabled: z.boolean().default(false),
  features: featuresSchema.default(featuresSchema.parse({})),
  frontend_development: z.boolean().default(false),
  frontend_host: z.string().default(''),
  billing_enabled: z.boolean().default(false),
  regions: regionsConfigSchema.optional(),
  regions_enabled: z.boolean().default(false),
  secret_options: secretOptionsSchema.default(secretOptionsSchema.parse({})),
  site_host: z.string().default(''),
  support_email: z.string().default(''),
  support_host: z.string().default(''),
  checkout_host: z.string().default(''),
  ui: uiInterfaceSchema.default(uiInterfaceSchema.parse({})),
  // First-class legal/policy URLs (site.legal, #4278). Always emitted by
  // ConfigSerializer ({} on a bare config), so `.default()` keeps the type
  // non-optional; the inner parse({}) fills each field's null default.
  legal: legalLinksSchema.default(legalLinksSchema.parse({})),
  available_jurisdictions: z.array(z.string()).default([]),

  // Frontend rendering config for the disabled-homepage view. All knobs
  // optional with auto-detection defaults; backend may omit entirely.
  disabled_homepage: disabledHomepageConfigSchema.default(disabledHomepageConfigSchema.parse({})),

  // ─────────────────────────────────────────────────────────────────────────────
  // Brand fields (per-installation defaults from OT.conf['brand'])
  //
  // Resolution order at the store layer:
  //   1. domain_branding.<field>           (per-domain, from Redis)
  //   2. bootstrapStore.brand_<field>      (per-installation, these fields)
  //   3. NEUTRAL_BRAND_DEFAULTS.<field>    (frontend neutral fallback)
  //
  // No `.default()` here by design — defaults flow through
  // NEUTRAL_BRAND_DEFAULTS at the store layer, not the schema. Eager
  // `.default()` would short-circuit the nullish-coalescing fallback chain.
  // ─────────────────────────────────────────────────────────────────────────────
  brand_primary_color: z.string().nullish(),
  brand_product_name: z.string().nullish(),
  brand_product_domain: z.string().nullish(),
  brand_support_email: z.string().nullish(),
  brand_corner_style: z.enum(cornerStyleValues).nullish(),
  brand_font_family: z.enum(fontFamilyValues).nullish(),
  brand_button_text_light: z.boolean().nullish(),
  brand_logo_url: z.string().nullish(),
  brand_logo_dark_url: z.string().nullish(),
  brand_logo_alt: z.string().nullish(),
  brand_favicon_url: z.string().nullish(),

  // ─────────────────────────────────────────────────────────────────────────────
  // AuthenticationSerializer fields
  // ─────────────────────────────────────────────────────────────────────────────
  apitoken: z.string().optional(),
  // The server's statement about the customer session (#4462). `.optional()`
  // with no default: a backend that predates the field omits it, and the
  // client then derives a status from the two booleans below — a derivation
  // that can only withhold. See effectiveAuthStatus().
  auth_status: authStatusSchema.optional(),
  // Compatibility projections of `auth_status`. The serializer computes both
  // FROM it, so the three never disagree on a payload from a current backend.
  authenticated: z.boolean().default(false),
  awaiting_mfa: z.boolean().optional().default(false),
  // Tri-state: true/false are definitive; null means the server could not
  // determine it (transient auth-DB failure during serialization). The store
  // treats null as "no information" and keeps the last known value.
  has_password: z.boolean().nullable().optional().default(false),
  // Policy axis independent of has_password (#3886): whether this account is
  // permitted to hold a local password. false only when SSO is enforced
  // (app-level restrict_to='sso' or per-domain enforce_sso_only) or auth mode
  // is not 'full'. Defaults true so consumer accounts keep the affordance.
  password_auth_permitted: z.boolean().default(true),
  custid: z.string().default(''),
  cust: bootstrapCustomerSchema.nullable().default(null),
  email: z.string().default(''),
  // customer_since: formatted date string (e.g., "Mar 21, 2026") from Ruby epochdom()
  customer_since: z.string().optional(),

  // Active impersonation overlay, or null. `.nullable().default(null)` rather
  // than `.optional()`: the serializer emits the key on every response (null
  // when inactive), and a always-present key is what lets a stop or an expiry
  // CLEAR the banner — bootstrapStore.update() filters undefined out of the
  // merge, so an omitted key would leave a stale marker on screen.
  impersonation: impersonationSchema.nullable().default(null),

  // ─────────────────────────────────────────────────────────────────────────────
  // DomainSerializer fields
  // ─────────────────────────────────────────────────────────────────────────────
  baseuri: z.string().default(''),
  canonical_domain: z.string().default(''),
  // Resolved link pool for the domain-context picker (#4063). Contract is
  // Array<String>, never null and never absent from a booted server: the
  // serializer's output_template seeds `[]` and DomainStrategy.link_domains
  // resolves an unset LINK_DOMAINS to [canonical_domain] server-side.
  //
  // `.default([])` — NOT `.nullable()`, and NOT `.optional()`:
  //  - a Zod default fires only for `undefined`, so an explicit `null` from a
  //    hypothetical nil-valued key would be a parse error, not a default.
  //    That is deliberate: it makes a Ruby-side regression loud.
  //  - `.optional()` would leave the key out of `bootstrapSchema.parse({})`,
  //    which is where the store's state shape comes from (bootstrapStore.ts:30),
  //    so Pinia would not track it reactively.
  //
  // `[]` therefore means exactly one thing to consumers: a stale pre-#4063
  // server (field absent). useDomainContext maps that back to
  // [canonicalDomain]. The frontend must never re-derive the unset case
  // itself — the server already resolved it.
  //
  // Values are normalized server-side (lowercased, port-stripped), so they
  // compare directly against `custom_domains` display_domain strings.
  // `canonical_domain` is NOT guaranteed to be a member: the canonical host
  // may be an internal platform address the picker deliberately hides.
  link_domains: z.array(z.string()).default([]),
  custom_domains: z.array(z.string()).optional().default([]),
  display_domain: z.string().default(''),
  domain_branding: brandSettingsCanonical.nullable().default(null),
  domain_context: z.string().nullish().default(null),
  homepage_config: homepageConfigCanonical.nullable().default(null),
  domain_id: z.string().default(''),
  domain_locale: z.string().nullable().default(null),
  domain_logo: z.string().nullable().default(null),
  domain_strategy: domainStrategySchema.default('canonical'),

  // ─────────────────────────────────────────────────────────────────────────────
  // I18nSerializer fields
  // ─────────────────────────────────────────────────────────────────────────────
  locale: z.string().default('en'),
  default_locale: z.string().default('en'),
  // OT.fallback_locale is the config's `i18n.fallback_locale` verbatim: a map
  // of locale -> fallback chain (the shape vue-i18n takes, see src/i18n.ts),
  // or a single locale string on a minimal config.
  fallback_locale: z
    .union([
      z.string(),
      z.record(
        z.string(),
        z.union([z.array(z.string()), z.string().transform((locale) => [locale])])
      ),
    ])
    .default('en'),
  supported_locales: z.array(z.string()).default([]),
  i18n_enabled: z.boolean().default(true),
  // Date/time display format: 'locale', 'iso8601', 'us', 'eu', 'eu-dot', 'uk',
  // 'long', or a date-fns pattern
  date_format: z.string().default('locale'),
  datetime_format: z.string().default('locale'),

  // ─────────────────────────────────────────────────────────────────────────────
  // MessagesSerializer fields
  // ─────────────────────────────────────────────────────────────────────────────
  messages: z.array(messageSchema).nullable().default([]),
  global_banner: z.string().nullable().default(null),
  // Audience scope for the global broadcast banner. Consumed by BaseLayout to
  // decide which page audiences (and whether custom domains) see the banner.
  // Mirrors Onetime::Operations::BannerState::VALID_SCOPES. Default 'no_recipient'
  // keeps legacy string-only banners off recipient pages and custom domains.
  global_banner_scope: z
    .enum(['all', 'no_recipient', 'workspace'])
    .nullable()
    .default('no_recipient'),

  // ─────────────────────────────────────────────────────────────────────────────
  // SystemSerializer fields
  // ─────────────────────────────────────────────────────────────────────────────
  ot_version: z.string().default(''),
  ot_version_long: z.string().default(''),
  ruby_version: z.string().default(''),
  shrimp: z.string().default(''),
  nonce: z.string().nullable().default(null),

  // Snapshot ordering (ADR-046). Present only on a complete snapshot of an
  // ORDERED session (authenticated or MFA-pending). ABSENT — never null —
  // for every other payload, for a degraded hydration whose allocation
  // failed, and from a backend that predates the contract. The epoch and the
  // version are validated as a unit by the superRefine below.
  //
  // `snapshot_version` is a decimal STRING, never a JSON number: compare with
  // BigInt. `snapshot_generated_at` is diagnostic only and deliberately has
  // NO format constraint here: a missing or malformed timestamp must not
  // fail the parse of a payload whose ordering pair is valid. Its format
  // (SNAPSHOT_GENERATED_AT_PATTERN) is checked separately, for diagnostics.
  snapshot_epoch: z.string().regex(SNAPSHOT_EPOCH_PATTERN).optional(),
  snapshot_version: z.string().regex(SNAPSHOT_VERSION_PATTERN).optional(),
  snapshot_generated_at: z.string().optional(),
  homepage_mode: z.string().nullable().default(null),
  enjoyTheVue: z.boolean().default(false),

  // ─────────────────────────────────────────────────────────────────────────────
  // OrganizationSerializer fields
  // ─────────────────────────────────────────────────────────────────────────────
  organization: organizationSchema.optional(),

  // ─────────────────────────────────────────────────────────────────────────────
  // DiagnosticsSerializer fields
  // ─────────────────────────────────────────────────────────────────────────────
  // Pseudonymous Sentry user context: a single `actor_ref`, the keyed one-way
  // digest of the customer extid. ABSENT (not empty, not null) for anonymous
  // sessions — see diagnosticsRefSchema above for the full rationale and for
  // why the inner object is strict rather than passthrough. Optional also
  // means an older backend that never emits the block degrades to
  // setUser(null) rather than failing the whole payload parse.
  diagnostics_ref: diagnosticsRefSchema.optional(),

  // ─────────────────────────────────────────────────────────────────────────────
  // Billing/Stripe fields
  // ─────────────────────────────────────────────────────────────────────────────
  stripe_customer: z.custom<Stripe.Customer>().optional(),
  stripe_subscriptions: z.array(z.custom<Stripe.Subscription>()).optional(),

  // ─────────────────────────────────────────────────────────────────────────────
  // Entitlement test mode (colonel only)
  // ─────────────────────────────────────────────────────────────────────────────
  entitlement_preview_planid: z.string().nullish(),
  entitlement_preview_plan_name: z.string().nullish(),

  // ─────────────────────────────────────────────────────────────────────────────
  // Development (always emitted by ConfigSerializer)
  // ─────────────────────────────────────────────────────────────────────────────
  development: developmentConfigSchema.default(developmentConfigSchema.parse({})),
})
  // ADR-046: the ordering pair is a unit — both present and valid, or both
  // absent. Zod 4 keeps the object type through a refinement, so `.shape`,
  // `parse({})` and `z.infer` are unchanged. JSON Schema cannot express the
  // rule; the generated schema validates each field's format only.
  .superRefine((payload, ctx) => {
    const hasEpoch = payload.snapshot_epoch !== undefined;
    const hasVersion = payload.snapshot_version !== undefined;
    if (hasEpoch === hasVersion) return;

    ctx.addIssue({
      code: 'custom',
      path: [hasEpoch ? 'snapshot_version' : 'snapshot_epoch'],
      message: 'snapshot_epoch and snapshot_version must both be present or both be absent',
    });
  });

// ═══════════════════════════════════════════════════════════════════════════════
// WIRE NULLS
// ═══════════════════════════════════════════════════════════════════════════════

let nullRejectingKeys: ReadonlySet<string> | undefined;

/**
 * Reads a server `null` as "not emitted" for every top-level key whose schema
 * does not accept null.
 *
 * Every Ruby serializer seeds its `output_template` with nil for each key it
 * owns and overwrites only what applies to the request, so ANY key can arrive
 * as null (`custid` for an anonymous visitor, `domains` with the feature off,
 * `support_email` on a bare config). The schema's `.default()` is the value a
 * consumer should see in that case, and Zod applies a default to `undefined`
 * only. Keys whose schema accepts null (`cust`, `organization`, ...) are left
 * alone, so a meaningful null stays a null.
 *
 * Apply this to a payload that came off the wire (hydration, /bootstrap/me)
 * before `bootstrapSchema.parse`. It never adds a key or changes a value, so
 * it cannot make a payload claim more than the server sent.
 */
export function withoutWireNulls(data: unknown): unknown {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) return data;

  nullRejectingKeys ??= new Set(
    Object.entries(bootstrapSchema.shape)
      .filter(([, field]) => !(field as z.ZodType).safeParse(null).success)
      .map(([key]) => key)
  );

  const rejecting = nullRejectingKeys;
  return Object.fromEntries(
    Object.entries(data).filter(([key, value]) => !(value === null && rejecting.has(key)))
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// BOOTSTRAP PAYLOAD TYPE
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * BootstrapPayload - the contract type for window.__BOOTSTRAP_ME__.
 *
 * Derived directly from the schema. All nested types are canonical contracts.
 */
export type BootstrapPayload = z.infer<typeof bootstrapSchema>;

/** Input type before defaults are applied. */
export type BootstrapPayloadInput = z.input<typeof bootstrapSchema>;

// Default export for Rhales tsx import mode
export default bootstrapSchema;
